/**
 * Service de génération de PDF pour les devis - Version améliorée
 * 
 * Ce service utilise les informations de la société OMAD SERVICES
 * pour générer des devis professionnels en PDF avec impression optimisée
 * 
 * Intègre toutes les corrections de récupération des données :
 * - Type de tarif robuste
 * - Frais de mandat calculés automatiquement  
 * - Dates récupérées correctement
 * - Affichage cohérent avec les modals
 */

import { getCompanyInfo, getDefaultCompanyInfo } from './companyInfoService';
import { formaterEuro, getFraisMandat } from './tarificationService';
import { formatAddress } from '../utils/addressFormatter';
import { db } from '../firebase';

/**
 * Fonction robuste pour récupérer le type de tarif (identique aux modals)
 */
const getBaseRateTypeFromDevis = (devis) => {
  // Ordre de priorité identique à DevisModalBase et DevisManagement
  if (devis.contratParams?.baseRateType) {
    console.log('PDF - Type de tarif récupéré depuis contratParams:', devis.contratParams.baseRateType);
    return devis.contratParams.baseRateType;
  }
  
  if (devis.calculs?.configuration?.baseRateType) {
    console.log('PDF - Type de tarif récupéré depuis calculs.configuration:', devis.calculs.configuration.baseRateType);
    return devis.calculs.configuration.baseRateType;
  }
  
  if (devis.calculs?.contrat?.baseRateType) {
    console.log('PDF - Type de tarif récupéré depuis calculs.contrat:', devis.calculs.contrat.baseRateType);
    return devis.calculs.contrat.baseRateType;
  }
  
  // Fallback : analyser le tarif de base pour déduire le type
  if (devis.calculs?.contrat?.tarifBase) {
    const tarifBase = devis.calculs.contrat.tarifBase;
    if (Math.abs(tarifBase - 17.17) < 0.1) {
      console.log('PDF - Type de tarif déduit depuis le tarif de base (17.17€): 70plus');
      return '70plus';
    }
    if (Math.abs(tarifBase - 24.41) < 0.1) {
      console.log('PDF - Type de tarif déduit depuis le tarif de base (24.41€): standard');
      return 'standard';
    }
  }
  
  console.warn('PDF - Impossible de récupérer le type de tarif, utilisation du défaut: standard');
  return 'standard';
};

/**
 * Récupération robuste des frais de mandat (identique aux modals)
 */
const getFraisMandatFromDevis = (devis) => {
  // Rechercher dans plusieurs sources
  const fraisMandat = devis.calculs?.contrat?.fraisMandat || 
                     devis.contratParams?.fraisMandat ||
                     (devis.calculs?.contrat?.volumeHebdo ? 
                       getFraisMandat(devis.calculs.contrat.volumeHebdo) : 0);
  return fraisMandat;
};

/**
 * Récupération robuste des dates (identique aux modals)
 */
const getDateFromDevis = (devis, type = 'creation') => {
  let date;
  
  if (type === 'creation') {
    date = devis.dateCreation || devis.calculs?.dateGeneration || devis.createdAt;
  } else if (type === 'generation') {
    date = devis.calculs?.dateGeneration || 
           devis.calculs?.configuration?.dateGeneration ||
           devis.dateCreation || 
           devis.createdAt;
  } else if (type === 'validite') {
    date = devis.dateValidite;
  }
  
  if (date) {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  }
  
  return 'N/A';
};

/**
 * Récupération de la période de prestation
 */
const getPeriodePrestationFromDevis = (devis) => {
  const dateDebut = devis.dateDebut;
  const dateFin = devis.dateFin;

  if (!dateDebut && !dateFin) return null;

  const formatDate = (date) => {
    if (!date) return null;
    const dateObj = typeof date.toDate === 'function' ? date.toDate() : (typeof date === 'string' ? new Date(date) : date);
    return dateObj.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const dateDebutObj = dateDebut ? (typeof dateDebut.toDate === 'function' ? dateDebut.toDate() : new Date(dateDebut)) : null;
  const dateFinObj = dateFin ? (typeof dateFin.toDate === 'function' ? dateFin.toDate() : new Date(dateFin)) : null;

  return {
    debut: formatDate(dateDebut),
    fin: formatDate(dateFin),
    duree: dateDebutObj && dateFinObj
      ? Math.ceil((dateFinObj - dateDebutObj) / (1000 * 60 * 60 * 24))
      : null
  };
};

/**
 * Génère le HTML d'un devis pour conversion en PDF
 * @param {Object} devis - Données du devis
 * @param {Object} companyInfo - Informations de la société
 * @returns {string} HTML du devis
 */
export const genererHTMLDevis = async (devis, companyInfo = null) => {
  // Récupérer les informations de la société si non fournies
  if (!companyInfo) {
    try {
      companyInfo = await getCompanyInfo(db);
    } catch (error) {
      console.warn('Impossible de récupérer les infos société, utilisation des valeurs par défaut');
      companyInfo = getDefaultCompanyInfo();
    }
  }

  // Récupérer le type de tarif de façon robuste
  const baseRateType = getBaseRateTypeFromDevis(devis);
  const isExonere = baseRateType === '70plus';
  const fraisMandat = getFraisMandatFromDevis(devis);

  // Obtenir le libellé du statut
  const getLibelleStatut = (statut) => {
    switch (statut) {
      case 'brouillon': return 'Brouillon';
      case 'envoye': return 'Envoyé';
      case 'accepte': return 'Accepté';
      case 'refuse': return 'Refusé';
      default: return statut;
    }
  };

  console.log('PDF - Génération avec type de tarif:', baseRateType, 'Frais mandat:', fraisMandat);

  return `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Devis ${devis.numero} - ${companyInfo.denominationSociale}</title>
    <style>
        /* Styles pour l'impression PDF */
        @page {
            margin: 20mm;
            size: A4;
        }
        
        * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
        }

        body {
            font-family: 'Arial', sans-serif;
            line-height: 1.4;
            color: #000000 !important;
            margin: 0;
            padding: 0;
            font-size: 12px;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
        }
        
        .container {
            max-width: 210mm;
            margin: 0 auto;
            padding: 0;
            padding-bottom: 30px;
        }
        
        /* En-tête */
        .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 20px;
            border-bottom: 2px solid #3b82f6;
            padding-bottom: 15px;
        }
        
        .company-info {
            flex: 1;
        }
        
        .company-logo {
            max-width: 80px;
            max-height: 80px;
            margin-bottom: 10px;
        }
        
        .company-name {
            font-size: 18px;
            font-weight: bold;
            color: #000000;
            margin-bottom: 5px;
        }
        
        .company-details {
            font-size: 11px;
            color: #000000 !important;
            line-height: 1.3;
            font-weight: 500 !important;
        }
        
        .devis-info {
            text-align: right;
        }
        
        .devis-title {
            font-size: 24px;
            font-weight: bold;
            color: #3b82f6;
            margin-bottom: 5px;
        }
        
        .devis-number {
            font-size: 14px;
            font-weight: bold;
            margin-bottom: 5px;
        }
        
        /* Sections */
        .section {
            margin-bottom: 20px;
        }
        
        .section-title {
            font-size: 15px;
            font-weight: 700 !important;
            color: #000000 !important;
            margin-bottom: 10px;
            border-bottom: 2px solid #000000 !important;
            padding-bottom: 5px;
        }
        
        /* Informations client - Style professionnel sans cadre */
        .client-info {
            padding: 0;
            margin: 0;
        }
        
        .client-name {
            font-weight: bold;
            font-size: 14px;
            margin-bottom: 8px;
            color: #000000;
        }

        .client-details {
            font-size: 12px;
            line-height: 1.5;
            color: #000000 !important;
            font-weight: normal !important;
        }

        .client-details div {
            color: #000000 !important;
        }

        .client-details strong {
            color: #000000 !important;
            font-weight: 600 !important;
        }
        
        .client-details div {
            margin-bottom: 3px;
        }
        
        /* Table des prestations */
        .prestations-table {
            width: 100%;
            border-collapse: collapse;
            margin-bottom: 20px;
        }
        
        .prestations-table th {
            background-color: #f1f5f9;
            padding: 8px;
            text-align: left;
            font-weight: 700 !important;
            border: 1px solid #e2e8f0;
            font-size: 11px;
            color: #000000 !important;
        }

        .prestations-table td {
            padding: 8px;
            border: 1px solid #e2e8f0;
            font-size: 11px;
            color: #000000 !important;
            font-weight: normal !important;
        }
        
        .prestation-type {
            font-weight: bold;
        }
        
        .text-right {
            text-align: right;
        }
        
        .text-center {
            text-align: center;
        }
        
        /* Totaux */
        .totals {
            width: 50%;
            margin-left: auto;
            margin-top: 20px;
        }
        
        .total-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            border-bottom: 1px solid #e5e7eb;
        }
        
        .total-final {
            font-weight: bold;
            font-size: 14px;
            border-top: 2px solid #3b82f6;
            margin-top: 5px;
            padding-top: 5px;
        }
        
        /* Conditions */
        .conditions {
            margin-top: 30px;
            padding-top: 15px;
            border-top: 1px solid #e5e7eb;
            font-size: 10px;
            color: #000000 !important;
            font-weight: normal !important;
        }

        /* Pied de page */
        .footer {
            margin-top: 40px;
            padding-top: 15px;
            padding-bottom: 15px;
            border-top: 1px solid #e5e7eb;
            font-size: 9px;
            color: #000000 !important;
            text-align: center;
            font-weight: normal !important;
            page-break-inside: avoid;
        }
        
        /* Utilitaires */
        .page-break {
            page-break-before: always;
        }
        
        .no-break {
            page-break-inside: avoid;
        }
        
        .bg-gray-50 {
            background-color: #f9fafb;
        }
        
        .font-bold {
            font-weight: bold;
        }
    </style>
</head>
<body>
    <div class="container">
        <!-- En-tête -->
        <div class="header no-break">
            <div class="company-info">
                ${companyInfo.logoUrl ? `<img src="${companyInfo.logoUrl}" alt="Logo ${companyInfo.denominationSociale}" class="company-logo">` : ''}
                <div class="company-name">${companyInfo.denominationSociale} (${companyInfo.sigle})</div>
                <div class="company-details">
                    ${companyInfo.formeJuridique}<br>
                    Capital : ${companyInfo.capitalSocial}<br>
                    ${companyInfo.siegeSocial}<br>
                    SIREN : ${companyInfo.siren} - SIRET : ${companyInfo.siret}<br>
                    ${companyInfo.numeroSAP ? `SAP : ${companyInfo.numeroSAP}<br>` : ''}
                    ${companyInfo.tvaIntracommunautaire ? `TVA Intracommunautaire : ${companyInfo.tvaIntracommunautaire}<br>` : ''}
                    ${companyInfo.telephone ? `Tél : ${companyInfo.telephone}` : ''}
                    ${companyInfo.email ? ` - Email : ${companyInfo.email}` : ''}
                    ${companyInfo.siteWeb ? ` - Site : ${companyInfo.siteWeb}` : ''}
                </div>
            </div>
            <div class="devis-info">
                <div class="devis-title">DEVIS</div>
                <div class="devis-number">N° ${devis.numero}</div>
                <div>Date : ${getDateFromDevis(devis, 'creation')}</div>
                <div>Validité : ${getDateFromDevis(devis, 'validite') !== 'N/A' ? getDateFromDevis(devis, 'validite') : '30 jours'}</div>
                <div>Statut : ${getLibelleStatut(devis.statut)}</div>
            </div>
        </div>

        <!-- Informations client -->
        <div class="section no-break">
            <div class="section-title">INFORMATIONS CLIENT</div>
            <div class="client-info">
                <div class="client-name">${devis.clientNom}</div>
                <div class="client-details">
                    <div><strong>Email :</strong> ${devis.clientEmail}</div>
                    ${devis.clientTelephone ? `<div><strong>Téléphone :</strong> ${devis.clientTelephone}</div>` : ''}
                    ${devis.clientAdresse ? `<div><strong>Adresse :</strong> ${formatAddress(devis.clientAdresse)}</div>` : ''}
                </div>
            </div>
        </div>

        <!-- Période de prestation -->
        ${(() => {
          const periode = getPeriodePrestationFromDevis(devis);
          if (!periode) return '';

          return `
        <div class="section no-break" style="background-color: #e0f2fe; border-left: 4px solid #0284c7; padding: 15px; border-radius: 8px; margin-bottom: 20px;">
            <div class="section-title" style="color: #0c4a6e;">📅 PÉRIODE DE PRESTATION</div>
            <div style="font-size: 12px; color: #000000 !important; line-height: 1.6;">
                ${periode.debut ? `<p><strong>Du :</strong> ${periode.debut}</p>` : ''}
                ${periode.fin ? `<p><strong>Au :</strong> ${periode.fin}</p>` : ''}
                ${periode.duree ? `<p><strong>Durée :</strong> ${periode.duree} jours</p>` : ''}
            </div>
        </div>
          `;
        })()}

        <!-- Détail des prestations avec frais détaillés -->
        <div class="section no-break">
            <div class="section-title">DÉTAIL DES PRESTATIONS ET COÛTS</div>
            <table class="prestations-table">
                <thead>
                    <tr>
                        <th>Prestation</th>
                        <th>Quantité</th>
                        <th>Tarif CESU</th>
                        <th>Frais mandat</th>
                        <th>Déplacement</th>
                        <th>Total ligne</th>
                    </tr>
                </thead>
                <tbody>
                    ${devis.calculs?.lignes ? devis.calculs.lignes.map((ligne, index) => {
                      // Récupérer les données originales de la ligne depuis lignesPrestations
                      const ligneOriginale = devis.lignesPrestations?.[index] || {};
                      const nbInterventions = ligneOriginale.nbInterventions || 1;
                      
                      return `
                    <tr>
                        <td class="prestation-type">
                            <div><strong>${ligne.typePrestation}</strong></div>
                            ${ligne.collaborateurNom ? `<div style="font-size: 9px; color: #000000 !important;">👤 ${ligne.collaborateurNom}</div>` : ''}
                            ${ligne.majorationAppliquee !== 'Aucune' ? `<div style="font-size: 9px; color: #000000 !important;">⚡ ${ligne.majorationAppliquee}</div>` : ''}
                            ${ligne.tarifPersonnalise ? `<div style="font-size: 9px; color: #000000 !important;">🎯 Tarif personnalisé</div>` : ''}
                            ${ligne.totalDeplacement > 0 ? `<div style="font-size: 9px; color: #000000 !important;">🚗 ${formaterEuro(ligne.fraisDeplacementUnitaire)} par intervention</div>` : ''}
                        </td>
                        <td class="text-center">
                            <div><strong>${ligne.heuresTotal}h</strong></div>
                            <div style="font-size: 10px; color: #000000 !important;">${ligne.quantite} ${ligne.quantite > 1 ? ligne.unite + 's' : ligne.unite}</div>
                            ${ligne.totalDeplacement > 0 ? `<div style="font-size: 10px; color: #000000 !important;">${nbInterventions} déplacement${nbInterventions > 1 ? 's' : ''}</div>` : ''}
                        </td>
                        <td class="text-right">
                            <div>${formaterEuro(ligne.tarifCesuUnitaire)}/h</div>
                            <div style="font-size: 10px; color: #000000 !important;">${formaterEuro(ligne.totalCesu)}</div>
                        </td>
                        <td class="text-right">
                            ${ligne.fraisMandatUnitaire > 0 ? `
                            <div>${formaterEuro(ligne.fraisMandatUnitaire)}/h</div>
                            <div style="font-size: 10px; color: #000000 !important;">${formaterEuro(ligne.totalMandat)}</div>
                            ` : '<div style="font-size: 10px; color: #000000 !important;">-</div>'}
                        </td>
                        <td class="text-right">
                            ${ligne.totalDeplacement > 0 ? `
                            <div>${formaterEuro(ligne.totalDeplacement)}</div>
                            <div style="font-size: 10px; color: #000000 !important;">${nbInterventions} × ${formaterEuro(ligne.fraisDeplacementUnitaire)}</div>
                            ` : '<div style="font-size: 10px; color: #000000 !important;">-</div>'}
                        </td>
                        <td class="text-right">
                            <div class="font-semibold">${formaterEuro(ligne.totalLigne)}</div>
                        </td>
                    </tr>
                    `}).join('') : ''}
                </tbody>
            </table>
        </div>

        <!-- Récapitulatif détaillé par intervenant -->
        <div class="section no-break">
            <div class="section-title">RÉCAPITULATIF DÉTAILLÉ PAR INTERVENANT</div>
            <div class="totals">
                ${devis.calculs?.totaux ? `
                <!-- Détail par collaborateur -->
                ${devis.calculs.totaux.parCollaborateur && devis.calculs.totaux.parCollaborateur.length > 0 ? 
                  devis.calculs.totaux.parCollaborateur.map(collab => {
                    const sousTotal = collab.totalCesu + collab.totalMandat + collab.totalDeplacement;
                    
                    return `
                <div class="total-row" style="border-bottom: 1px dashed #e5e7eb; padding-bottom: 8px; margin-bottom: 8px;">
                    <span><strong>${collab.collaborateur.nom}</strong></span>
                    <span><strong>${formaterEuro(sousTotal)}</strong></span>
                </div>
                <div class="total-row">
                    <span style="padding-left: 15px;">- Salaire & charges CESU</span>
                    <span>${formaterEuro(collab.totalCesu)}</span>
                </div>
                ${collab.totalMandat > 0 ? `
                <div class="total-row">
                    <span style="padding-left: 15px;">- Frais de mandat OMAD</span>
                    <span>${formaterEuro(collab.totalMandat)}</span>
                </div>
                ` : ''}
                ${collab.totalDeplacement > 0 ? `
                <div class="total-row">
                    <span style="padding-left: 15px;">- Frais de déplacement</span>
                    <span>${formaterEuro(collab.totalDeplacement)}</span>
                </div>
                ` : ''}
                ${collab.detailHeuresParTaux && collab.detailHeuresParTaux.length > 0 ?
                  collab.detailHeuresParTaux.map(detail => `
                <div class="total-row" style="background-color: #f9fafb; padding: 4px; border-radius: 3px; margin: 3px 0;">
                    <span style="padding-left: 20px; font-size: 9px;">
                        ${detail.heures}h ${detail.majoration !== 'Aucune' ? detail.majoration : 'à taux normal'} (${formaterEuro(detail.tauxHoraire)}/h)
                    </span>
                    <span style="font-size: 9px;">${formaterEuro(detail.montantCesu)}</span>
                </div>
                  `).join('') : ''}
                <div class="total-row">
                    <span style="padding-left: 15px;">- Total heures prestées</span>
                    <span>${collab.totalHeures}h</span>
                </div>
                ${collab.collaborateur.isAutoEntrepreneur ? `
                <div class="total-row" style="background-color: #f0f9ff; padding: 4px; border-radius: 4px; margin: 5px 0;">
                    <span style="padding-left: 15px; font-size: 9px; color: #1e40af;">
                        🎯 Auto-entrepreneur - Tarif personnalisé
                    </span>
                </div>
                ` : ''}
                `}).join('') : `
                <!-- Si pas de collaborateurs spécifiques, afficher les totaux globaux -->
                <div class="total-row">
                    <span>Salaire & charges CESU :</span>
                    <span>${formaterEuro(devis.calculs.totaux.totalCesu)}</span>
                </div>
                ${devis.calculs.totaux.totalMandat > 0 ? `
                <div class="total-row">
                    <span>Frais de mandat OMAD :</span>
                    <span>${formaterEuro(devis.calculs.totaux.totalMandat)}</span>
                </div>
                ` : ''}
                ${devis.calculs.totaux.totalDeplacement > 0 ? `
                <div class="total-row">
                    <span>Frais de déplacement :</span>
                    <span>${formaterEuro(devis.calculs.totaux.totalDeplacement)}</span>
                </div>
                ` : ''}
                <div class="total-row">
                    <span>Total heures prestées :</span>
                    <span>${devis.calculs.totaux.totalHeures}h</span>
                </div>
                `}
                
                <!-- Total HT (Services à la personne exonérés de TVA) -->
                <div class="total-row" style="border-top: 2px solid #3b82f6; margin-top: 15px; padding-top: 10px;">
                    <span><strong>TOTAL HT :</strong></span>
                    <span><strong>${formaterEuro(devis.montantTTC)}</strong></span>
                </div>
                <div class="total-row" style="background-color: #f0f9ff; padding: 8px; border-radius: 4px; margin-top: 5px;">
                    <span style="font-size: 10px; color: #1e40af;">
                        <strong>Exonération de TVA</strong> - Services à la personne (art. 261-4-4° b CGI)
                    </span>
                </div>
                ` : `
                <div class="total-row total-final">
                    <span><strong>TOTAL HT :</strong></span>
                    <span><strong>${formaterEuro(devis.montantTTC)}</strong></span>
                </div>
                `}
            </div>
        </div>

        <!-- Informations intervenants -->
        ${devis.calculs?.collaborateurs && devis.calculs.collaborateurs.length > 0 ? `
        <div class="section no-break" style="background-color: #f0f9ff; border-left: 4px solid #3b82f6; padding: 15px; border-radius: 8px;">
            <div class="section-title" style="color: #1e40af;">👥 INTERVENANTS ASSIGNÉS</div>
            <div style="font-size: 11px; color: #000000 !important; line-height: 1.8;">
                ${devis.calculs.collaborateurs.map(collab => `
                    <div style="margin-bottom: 10px;">
                        <strong>👤 ${collab.nom}</strong>
                        ${collab.cesuNumber ? `<br><span style="margin-left: 20px;">🔖 Numéro CESU : ${collab.cesuNumber}</span>` : ''}
                        ${collab.nanCrcesu ? `<br><span style="margin-left: 20px;">🏢 Code NAN CRCESU : ${collab.nanCrcesu}</span>` : ''}
                        ${collab.isAutoEntrepreneur ? `<br><span style="margin-left: 20px;">💼 Auto-entrepreneur</span>` : ''}
                    </div>
                `).join('')}
            </div>
        </div>
        ` : ''}

        <!-- Type de tarif appliqué - Version robuste -->
        <div class="section no-break" style="background-color: ${isExonere ? '#ecfdf5' : '#f0f9ff'}; border-left: 4px solid ${isExonere ? '#10b981' : '#3b82f6'}; padding: 15px; border-radius: 8px;">
            <div class="section-title" style="color: ${isExonere ? '#047857' : '#1e40af'};">🎯 TYPE DE TARIF APPLIQUÉ</div>
            <div style="font-size: 11px; color: #000000 !important; line-height: 1.5;">
                <p><strong>Type :</strong> ${isExonere ? '70+ (EXONÉRÉ)' : 'STANDARD'}</p>
                <p><strong>Tarif de base :</strong> ${formaterEuro(devis.calculs?.contrat?.tarifBase || (isExonere ? 17.17 : 24.41))}/h</p>
                <p><strong>Frais de mandat OMAD :</strong> ${formaterEuro(fraisMandat)}/h</p>
                <p><strong>Volume hebdomadaire :</strong> ${devis.calculs?.contrat?.volumeHebdo || 'N/A'}h/sem</p>
                ${isExonere ? `
                <p style="margin-top: 8px; font-style: italic; color: #000000 !important;">
                    ✅ Tarif exonéré de charges sociales pour bénéficiaires de 70 ans et plus
                </p>
                ` : `
                <p style="margin-top: 8px; font-style: italic; color: #000000 !important;">
                    📋 Tarif standard avec charges sociales incluses
                </p>
                `}
                <p style="margin-top: 8px; font-size: 9px; color: #000000 !important;">
                    Source du type de tarif : ${(() => {
                      if (devis.contratParams?.baseRateType) return 'Paramètres contrat';
                      if (devis.calculs?.configuration?.baseRateType) return 'Configuration calculs';
                      if (devis.calculs?.contrat?.baseRateType) return 'Contrat calculs';
                      return 'Déduit du tarif de base';
                    })()}
                </p>
            </div>
        </div>

        <!-- Informations sur le crédit d'impôt -->
        <div class="section no-break" style="background-color: #f0f9ff; border-left: 4px solid #3b82f6; padding: 15px; border-radius: 8px;">
            <div class="section-title" style="color: #000000 !important;">💡 INFORMATIONS CRÉDIT D'IMPÔT</div>
            <div style="font-size: 11px; color: #000000 !important; line-height: 1.5;">
                <p><strong>Crédit d'impôt de 50% :</strong> S'applique uniquement sur les prestations de services à la personne</p>
                <p><strong>Montant éligible :</strong> ${formaterEuro(devis.calculs?.totaux?.totalCesu + devis.calculs?.totaux?.totalMandat)}</p>
                <p><strong>Crédit d'impôt estimé :</strong> ${formaterEuro((devis.calculs?.totaux?.totalCesu + devis.calculs?.totaux?.totalMandat) * 0.5)}</p>
                <p style="margin-top: 8px; font-style: italic; color: #000000 !important;">
                    Le crédit d'impôt s'applique uniquement sur le salaire CESU et les frais de mandat OMAD.
                    Les frais de déplacement ne sont pas éligibles au crédit d'impôt.
                </p>
            </div>
        </div>

        <!-- Conditions générales -->
        ${companyInfo.conditionsVente ? `
        <div class="section no-break">
            <div class="section-title">CONDITIONS GÉNÉRALES DE VENTE</div>
            <div class="conditions">
                ${companyInfo.conditionsVente.replace(/\n/g, '<br>')}
            </div>
        </div>
        ` : ''}

        <!-- Mentions légales -->
        ${companyInfo.mentionsLegales ? `
        <div class="section no-break">
            <div class="section-title">MENTIONS LÉGALES</div>
            <div class="conditions">
                ${companyInfo.mentionsLegales.replace(/\n/g, '<br>')}
            </div>
        </div>
        ` : ''}

        <!-- Pied de page -->
        <div class="footer">
            ${companyInfo.denominationSociale} - ${companyInfo.siegeSocial} - 
            SIREN : ${companyInfo.siren} - SIRET : ${companyInfo.siret}
            ${companyInfo.numeroSAP ? ` - SAP : ${companyInfo.numeroSAP}` : ''}
            ${companyInfo.tvaIntracommunautaire ? ` - TVA : ${companyInfo.tvaIntracommunautaire}` : ''}
        </div>
    </div>
</body>
</html>
  `;
};

/**
 * Génère et télécharge un devis en PDF
 * @param {Object} devis - Données du devis
 * @param {Object} options - Options de génération
 */
export const genererPDFDevis = async (devis, options = {}) => {
  try {
    // Vérifier si html2pdf est disponible
    if (typeof window === 'undefined') {
      throw new Error('html2pdf n\'est disponible que côté client');
    }

    // Importer html2pdf dynamiquement
    const html2pdf = await import('html2pdf.js');
    
    // Récupérer les informations de la société
    const companyInfo = options.companyInfo || await getCompanyInfo(db);
    
    // Générer le HTML du devis
    const htmlContent = await genererHTMLDevis(devis, companyInfo);
    
    // Créer un élément temporaire pour le contenu
    const element = document.createElement('div');
    element.innerHTML = htmlContent;
    
    // Options de configuration pour html2pdf
    const pdfOptions = {
      margin: 10,
      filename: `Devis_${devis.numero}_${companyInfo.sigle}.pdf`,
      image: { type: 'jpeg', quality: 1.0 },
      html2canvas: {
        scale: 3,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
        windowHeight: 1600,
        scrollY: 0,
        scrollX: 0,
        allowTaint: true,
        letterRendering: true
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: false
      },
      ...options.pdfOptions
    };
    
    // Vérifier si on doit retourner un Blob au lieu de télécharger
    if (options.returnBlob) {
      // Générer le PDF et retourner le Blob
      const pdfBlob = await html2pdf.default()
        .set(pdfOptions)
        .from(element)
        .output('blob');
      
      return pdfBlob;
    } else {
      // Comportement par défaut : télécharger le PDF
      await html2pdf.default()
        .set(pdfOptions)
        .from(element)
        .save();
      
      return true;
    }
  } catch (error) {
    console.error('Erreur lors de la génération du PDF:', error);
    throw error;
  }
};

/**
 * Ouvre le devis dans une nouvelle fenêtre pour impression
 * @param {Object} devis - Données du devis
 * @param {Object} options - Options d'impression
 */
export const imprimerDevis = async (devis, options = {}) => {
  try {
    // Générer le HTML du devis
    const htmlContent = await genererHTMLDevis(devis, options.companyInfo);
    
    // Créer une nouvelle fenêtre pour l'impression
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Attendre que le contenu soit chargé puis imprimer
    printWindow.onload = () => {
      printWindow.print();
      // Fermer la fenêtre après impression (optionnel)
      if (options.autoClose !== false) {
        setTimeout(() => printWindow.close(), 500);
      }
    };
    
    return true;
  } catch (error) {
    console.error('Erreur lors de l\'impression:', error);
    throw error;
  }
};

/**
 * Retourne l'URL d'un PDF généré (pour prévisualisation)
 * @param {Object} devis - Données du devis
 * @param {Object} options - Options de génération
 * @returns {Promise<string>} URL du PDF
 */
export const genererURLPDFDevis = async (devis, options = {}) => {
  try {
    // Vérifier si html2pdf est disponible
    if (typeof window === 'undefined') {
      throw new Error('html2pdf n\'est disponible que côté client');
    }

    // Importer html2pdf dynamiquement
    const html2pdf = await import('html2pdf.js');
    
    // Générer le HTML du devis
    const htmlContent = await genererHTMLDevis(devis, options.companyInfo);
    
    // Créer un élément temporaire pour le contenu
    const element = document.createElement('div');
    element.innerHTML = htmlContent;
    
    // Options de configuration pour html2pdf
    const pdfOptions = {
      margin: 10,
      image: { type: 'jpeg', quality: 1.0 },
      html2canvas: {
        scale: 3,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: 1200,
        windowHeight: 1600,
        scrollY: 0,
        scrollX: 0,
        allowTaint: true,
        letterRendering: true
      },
      jsPDF: {
        unit: 'mm',
        format: 'a4',
        orientation: 'portrait',
        compress: false
      },
      ...options.pdfOptions
    };
    
    // Générer le PDF et retourner l'URL
    const pdf = await html2pdf.default()
      .set(pdfOptions)
      .from(element)
      .output('bloburl');
    
    return pdf;
  } catch (error) {
    console.error('Erreur lors de la génération de l\'URL PDF:', error);
    throw error;
  }
};

export default {
  genererHTMLDevis,
  genererPDFDevis,
  imprimerDevis,
  genererURLPDFDevis
};
